// atriumNav.tsx — Atrium in-page navigation context (Sprint 88B, ADR-082).
// ----------------------------------------------------------------------------
// The Atrium shell owns no router; page selection is the shared `selected`
// page-key state in MainDashboard, mutated via `selectAndReveal`. The Atrium
// home components render as `children` of AtriumShell (NOT through props), so
// they had no way to drive navigation — which is why the Owner home's
// "Make a payment" / "New request" CTAs were dead no-ops (E2E M2).
//
// This lightweight context lets any in-shell page request a navigation to
// another page key WITHOUT prop-drilling through AtriumShell + DashboardRouter.
// MainDashboard provides `selectAndReveal` as the value; consumers call
// `useAtriumNav()` and get a stable `go(key)` function (a safe no-op when no
// provider is present, e.g. legacy shell — so nothing breaks flag-off).
import React, { createContext, useContext } from 'react';
import type { DashboardComponent } from '../dashboard/menuConfig';

export interface AtriumNav {
  /** Navigate the shell to a page key (auto-expands its nav group). */
  go: (key: DashboardComponent) => void;
}

const noop: AtriumNav = { go: () => { /* no provider — legacy shell */ } };

const AtriumNavContext = createContext<AtriumNav>(noop);

export const AtriumNavProvider: React.FC<{
  go: (key: DashboardComponent) => void;
  children: React.ReactNode;
}> = ({ go, children }) => (
  <AtriumNavContext.Provider value={{ go }}>{children}</AtriumNavContext.Provider>
);

/** Access the shell navigator. Returns a no-op `go` when outside an Atrium shell. */
export function useAtriumNav(): AtriumNav {
  return useContext(AtriumNavContext);
}
